// ui/autogrow.js — 여러 줄 입력이 **쓴 만큼 자라는** 규칙.
//
// 손잡이를 끌어 크기를 바꾸는 textarea 는 대화상자 안에서 곧바로 문제가 된다: 사람이
// 늘리면 제출 버튼이 화면 밖으로 밀리고, 줄이면 자기가 쓴 글이 두 줄만 보인다.
// 그래서 크기는 **내용이 정하고**, 상한을 넘으면 그 안에서 스크롤한다.
//
// 계산은 여기 순수 함수에 있고 DOM 을 만지지 않는다. 「상한을 넘으면 스크롤이 생긴다」
// 같은 문장을 브라우저 없이 값으로 확인할 수 있어야 하기 때문이다.

/**
 * 한 줄 높이 × 줄 수 + 상자 여백.
 *
 * `lineHeight` · `padding` 은 호출자가 실측해서 넘긴다 — 여기서 토큰을 다시 읽으면
 * CSS 와 JS 두 곳에 같은 숫자가 생기고, 언젠가 한쪽만 바뀐다.
 */
export function maxHeightForRows({ rows, lineHeight, verticalPadding = 0 }) {
  if (!Number.isFinite(rows) || !Number.isFinite(lineHeight) || rows <= 0) return null;
  return rows * lineHeight + verticalPadding;
}

/**
 * 지금 내용에 맞는 높이와 넘침 처리.
 *
 * @param {number} scrollHeight 내용이 요구하는 높이
 * @param {number|null} maxHeight 상한. null 이면 상한 없음
 * @param {number} minHeight 접혔을 때의 최소 높이
 * @returns {{ height: number, overflowY: 'auto'|'hidden', clamped: boolean }}
 */
export function autoGrowHeight({ scrollHeight, maxHeight = null, minHeight = 0 }) {
  const content = Number.isFinite(scrollHeight) ? scrollHeight : 0;
  const wanted = Math.max(content, minHeight);
  if (maxHeight !== null && wanted > maxHeight) {
    // 상한에 닿았다 — 여기서부터는 상자가 아니라 내용이 움직인다.
    return { height: maxHeight, overflowY: 'auto', clamped: true };
  }
  return { height: wanted, overflowY: 'hidden', clamped: false };
}

/**
 * 실제 요소에 적용한다. **먼저 `auto` 로 되돌리는 것**이 핵심이다 — 그러지 않으면
 * `scrollHeight` 가 직전에 우리가 정한 높이를 그대로 되읽어서, 글을 지워도 줄어들지 않는다.
 */
export function applyAutoGrow(element, { maxHeight = null } = {}) {
  if (!element || typeof element.style === 'undefined') return null;
  element.style.height = 'auto';
  const result = autoGrowHeight({ scrollHeight: element.scrollHeight, maxHeight });
  element.style.height = `${result.height}px`;
  element.style.overflowY = result.overflowY;
  return result;
}
